import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Action, EnrichedAction } from 'src/app/core/models/action.model';
import { Kyc_Check } from 'src/app/core/models/kyc_check.model';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { UserService } from 'src/app/core/services/user.service';
import { notesAllowProceed } from 'src/app/core/utils/kyc-postpone.util';
import { ManagementStepService } from '../../../pages/guide/services/managementStep.service';

@Component({
  selector: 'app-outstanding-actions',
  templateUrl: './outstanding-actions.component.html',
  styleUrls: [
    './outstanding-actions.component.scss',
    './components/oa-styles.scss',
  ],
})
export class OutstandingActionsComponent implements OnInit {
  @Input() claimId!: string;
  @Output() outstandingActions = new EventEmitter<Action[]>();

  loading: boolean = true;
  error: string = '';
  actions?: Action[] | null;

  constructor(
    private cs: ClaimsService,
    private userService: UserService,
    private managementStepService: ManagementStepService
  ) {}
  
  async ngOnInit() {
    await this.loadActions();
  }

  async loadActions() {
    this.loading = true;
    
    // NOVO: Usar getOutstandingActionsV3 que trabalha com case_actions
    // Retorna apenas estrutura (sem status)
    const enrichedActions = await this.cs.getOutstandingActionsV3(this.claimId);
    console.log('[OA] loadActions (V3)', enrichedActions);

    if (!enrichedActions || enrichedActions.length === 0) {
      this.error = 'Could not load outstanding actions. Try again later.';
      this.loading = false;
      return;
    }

    // ✅ Buscar status de TODAS as actions (não apenas KYC)
    const user = await this.userService.getCurrentUser();
    const userId = user?.id;
    
    if (!userId) {
      console.error('[OA] No user found');
      this.loading = false;
      return;
    }

    // ✅ Preencher status de todas as actions
    await this.fillActionStatuses(enrichedActions, userId);
    
    // ✅ Processar KYCs ANTES de verificar pré-requisitos
    const kycs = enrichedActions.filter((e) => e.action_type === 'KYC');
    console.log('[OA] Kycs', kycs);
    
    const kycChecks = await this.resolveKycChecks(kycs, userId);

    // CPK-4442: KYC FAILED mas adiado (postpone) não pode aparecer como
    // concluído no card — marca a action para o oa-kyc renderizar como
    // pendente e o contador do os-actions-case contar como pending.
    const bypassedByAgent = !!(user?.attributes as any)?.kyc?.bypassedByAgent;
    this.markPostponedKycs(kycs, kycChecks, bypassedByAgent);

    // ✅ Atualizar prstatus verificando se pré-requisitos foram completados
    await this.updatePrerequisiteStatuses(enrichedActions, userId);
    
    const selectedKyc =
      kycs.length > 0 ? kycs[this.selectKycFromList(kycChecks)] : null;

    console.log('[OA] Selected KYC', selectedKyc?.case_action_id);

    const finalList = enrichedActions.filter(
      (e) => e.action_type !== 'KYC' || e === selectedKyc
    );

    // Converter EnrichedAction para Action (compatibilidade)
    this.actions = finalList.map((ea) => this.enrichedToAction(ea));
    
    this.loading = false;
    console.log('[OA] - Load (final)', this.actions);

    this.outstandingActions.emit(this.actions);
  }

  /**
   * Resolve o status de cada KYC action delegando para o mesmo método usado
   * pelo guide (ManagementStepService.checkAndFillKycStatus), em vez de manter
   * uma segunda query divergente aqui — card e guide ficavam em desacordo
   * porque cada um filtrava cp_kyc_check por critérios diferentes (action_id
   * aqui vs provider/cross-case lá, CPK-4054). O método já preenche
   * kyc.ac_status / kyc.done_action_id / kyc.status por referência; o retorno
   * (checkedRow) alimenta selectKycFromList para desempatar entre múltiplas
   * KYC actions do mesmo case.
   */
  private async resolveKycChecks(
    kycs: EnrichedAction[],
    userId: string
  ): Promise<(Kyc_Check | null)[]> {
    const kycChecks: (Kyc_Check | null)[] = [];

    for (const kyc of kycs) {
      const result = await this.managementStepService.checkAndFillKycStatus(
        kyc as unknown as Action,
        this.claimId,
        userId
      );
      kycChecks.push((result.checkedRow as any as Kyc_Check) ?? null);
    }

    return kycChecks;
  }

  /**
   * CPK-4442: marca as KYC actions cujo check resolvido FALHOU mas foi adiado
   * (postpone CPK-3620, notes com postpone_reason.canProceed=true). O
   * checkAndFillKycStatus deixa essas actions com ac_status='DONE' de propósito
   * — o postpone existe para NÃO travar os pré-requisitos (contrato etc.) — mas
   * no card elas não podem aparecer com check verde: seguem pendentes até o KYC
   * de fato passar. O flag `postponed` é lido pelo oa-kyc (renderiza como
   * PENDING) e pelo os-actions-case (conta como pending action).
   *
   * Bypass de agente continua aparecendo como concluído. O skip de agente
   * (CPK-4051) nunca entra aqui: notes é objeto, notesAllowProceed exige array.
   */
  private markPostponedKycs(
    kycs: EnrichedAction[],
    kycChecks: (Kyc_Check | null)[],
    bypassedByAgent: boolean
  ): void {
    if (bypassedByAgent) return;

    kycs.forEach((kyc, i) => {
      const check = kycChecks[i];
      if (
        check &&
        check.stage === 'DONE' &&
        check.passed === false &&
        notesAllowProceed((check as any).notes)
      ) {
        kyc.postponed = true;
        console.log('[OA] KYC postponed — displaying as pending (CPK-4442)', {
          caseActionId: kyc.case_action_id,
          kycCheckId: check.id,
        });
      }
    });
  }

  /**
   * Preenche status de todas as actions (Questionários e Assinaturas)
   * ✅ Similar ao ManagementStepService mas busca TODAS de uma vez
   * ✅ Para assinaturas, busca em TODAS as versões do contrato mas retorna apenas SIGNED
   */
  private async fillActionStatuses(
    actions: EnrichedAction[],
    userId: string
  ): Promise<void> {
    // Separar por tipo
    const questionnaires = actions.filter(a => a.action_type === 'QUESTIONNAIRE');
    const signatures = actions.filter(a => a.action_type === 'SIGNATURE');

    // 1️⃣ Para assinaturas, buscar contratos e suas versões anteriores
    const contractIdsMap = new Map<string, string[]>(); // contractId atual -> [atual, ...anteriores]
    
    if (signatures.length > 0) {
      const contractIds = signatures.map(s => s.contract_id!).filter(id => !!id);
      
      // Busca todos os contratos de uma vez
      const { data: contracts } = await this.cs.supabase
        .from('cp_contract')
        .select('id, attributes')
        .in('id', contractIds);
      
      // Monta mapa com versões
      for (const contract of contracts || []) {
        const allVersions = [contract.id];
        
        if (contract.attributes?.previous_versions && Array.isArray(contract.attributes.previous_versions)) {
          allVersions.push(...contract.attributes.previous_versions);
        }
        
        contractIdsMap.set(contract.id, allVersions);
      }
      
      console.log('[OA] Contract versions map', {
        totalContracts: contractIds.length,
        contractsWithHistory: Array.from(contractIdsMap.entries()).filter(([_, versions]) => versions.length > 1).length,
      });
    }

    // 2️⃣ Buscar status em paralelo
    const [questionnaireStatuses, signatureStatuses] = await Promise.all([
      // Questionários
      questionnaires.length > 0
        ? this.cs.supabase
            .from('cp_questionnaire_answer')
            .select('id, questionnaire_id, status')
            .eq('claim_id', this.claimId)
            .in('status', ['DONE', 'SUBMITTED'])
            .in(
              'questionnaire_id',
              questionnaires.map(q => q.questionnaire_id!).filter(id => !!id)
            )
        : Promise.resolve({ data: [], error: null }),

      // Assinaturas - busca por TODAS as versões dos contratos, mas apenas status SIGNED
      signatures.length > 0
        ? this.cs.supabase
            .from('cp_signed_contract')
            .select('id, contract_id, status')
            .eq('claim_id', this.claimId)
            .eq('signer_id', userId)
            .eq('status', 'SIGNED')
            .in(
              'contract_id',
              Array.from(new Set(
                Array.from(contractIdsMap.values()).flat()
              ))
            )
        : Promise.resolve({ data: [], error: null }),
    ]);

    // 3️⃣ Mapear questionários
    if (questionnaireStatuses.data) {
      for (const qa of questionnaireStatuses.data) {
        const action = questionnaires.find(
          a => a.questionnaire_id === qa.questionnaire_id
        );
        if (action) {
          action.done_action_id = qa.id;
          action.ac_status = qa.status;
          action.status = qa.status;
        }
      }
    }

    // 4️⃣ Mapear assinaturas (busca em todas versões mas aceita apenas SIGNED)
    if (signatureStatuses.data) {
      for (const action of signatures) {
        const versionsToCheck = contractIdsMap.get(action.contract_id!) || [action.contract_id!];
        
        // Procura assinatura SIGNED em qualquer versão do contrato
        const signedContract = signatureStatuses.data.find(
          sc => sc.contract_id && versionsToCheck.includes(sc.contract_id)
        );
        
        console.log('[OA] Checking signature for action', {
          actionContractId: action.contract_id,
          versionsToCheck,
          foundSignedContract: signedContract ? {
            id: signedContract.id,
            contract_id: signedContract.contract_id,
            status: signedContract.status,
          } : null,
        });

        if (signedContract) {
          action.done_action_id = signedContract.id;
          action.ac_status = signedContract.status;
          action.status = signedContract.status;
          
          console.log('[OA] Signature found', {
            actionContractId: action.contract_id,
            signedContractId: signedContract.contract_id,
            status: signedContract.status,
            isFromPreviousVersion: signedContract.contract_id !== action.contract_id,
          });
        }
      }
    }

    console.log('[OA] Statuses filled', {
      questionnaires: questionnaires.length,
      signatures: signatures.length,
      questionnairesFilled: questionnaires.filter(q => q.ac_status).length,
      signaturesFilled: signatures.filter(s => s.ac_status).length,
    });
  }

  /**
   * Atualiza o prstatus das actions verificando se os pré-requisitos foram
   * completados de forma TRANSITIVA.
   *
   * Uma action só é destravada (prstatus = 'DONE') quando TODOS os seus
   * pré-requisitos diretos estão satisfeitos — e um pré-requisito só conta como
   * satisfeito quando ele próprio está concluído E os pré-requisitos dele também
   * estão satisfeitos (recursivamente). Ver isPrereqSatisfied.
   */
  private async updatePrerequisiteStatuses(
    actions: EnrichedAction[],
    userId: string
  ): Promise<void> {
    console.log('[OA] Updating prerequisite statuses');

    // Buscar pré-requisitos da tabela cp_action_prerequisites
    const caseActionIds = actions.map(a => a.case_action_id);
    const { data: prerequisites, error } = await this.cs.supabase
      .from('cp_action_prerequisites')
      .select('action_id, prereq_id')
      .in('action_id', caseActionIds)
      .in('prereq_id', caseActionIds);

    if (error) {
      console.error('[OA] Error fetching prerequisites', error);
      return;
    }

    console.log('[OA] Prerequisites fetched', {
      total: prerequisites?.length || 0,
    });

    // Indexar actions por case_action_id e montar o grafo de pré-requisitos
    // (case_action_id → lista de case_action_ids que são pré-requisito dele).
    const actionByCaseActionId = new Map<string, EnrichedAction>();
    actions.forEach(a => actionByCaseActionId.set(a.case_action_id, a));

    const prereqMap = new Map<string, string[]>();
    (prerequisites || []).forEach(p => {
      if (!p.action_id || !p.prereq_id) return;
      const list = prereqMap.get(p.action_id) || [];
      list.push(p.prereq_id);
      prereqMap.set(p.action_id, list);
    });

    // Para cada action, prstatus = 'DONE' só quando TODOS os pré-requisitos
    // diretos estão satisfeitos de forma transitiva. Antes só o status direto do
    // pré-requisito era verificado, então um KYC reaproveitado de outro caso
    // (cross-case, ac_status='DONE') destravava o Contrato mesmo com os
    // questionários — pré-requisitos do próprio KYC — ainda pendentes.
    for (const action of actions) {
      const directPrereqs = prereqMap.get(action.case_action_id) || [];

      if (directPrereqs.length === 0) {
        // Sem pré-requisitos, marca como DONE
        action.prstatus = 'DONE';
        continue;
      }

      const allPrereqsComplete = directPrereqs.every(prereqId =>
        this.isPrereqSatisfied(prereqId, actionByCaseActionId, prereqMap, new Set())
      );

      action.prstatus = allPrereqsComplete ? 'DONE' : 'OPEN';

      // CPK-4864: o tooltip de bloqueio mostra o nome das actions que faltam,
      // em vez da description interna cadastrada na action.
      action.blocking_prereq_names = allPrereqsComplete
        ? []
        : this.collectBlockingPrereqNames(action.case_action_id, actionByCaseActionId, prereqMap);

      console.log('[OA] Prerequisite status updated', {
        actionId: action.action_id,
        actionName: action.action_name,
        prereqsCount: directPrereqs.length,
        allComplete: allPrereqsComplete,
        prstatus: action.prstatus,
        blockingPrereqNames: action.blocking_prereq_names,
      });
    }
  }

  /**
   * CPK-4864: nomes das actions que o cliente consegue concluir AGORA para
   * destravar `caseActionId`. Percorre o grafo de pré-requisitos de forma
   * transitiva e devolve só as "acionáveis": incompletas e com os próprios
   * pré-requisitos satisfeitos. Nomear um pré-requisito direto que já foi
   * concluído (mas que ainda depende de outra action) confundiria o cliente.
   */
  private collectBlockingPrereqNames(
    caseActionId: string,
    actionByCaseActionId: Map<string, EnrichedAction>,
    prereqMap: Map<string, string[]>
  ): string[] {
    const names: string[] = [];
    const visited = new Set<string>([caseActionId]);

    const walk = (id: string) => {
      for (const prereqId of prereqMap.get(id) || []) {
        if (visited.has(prereqId)) continue;
        visited.add(prereqId);

        if (this.isPrereqSatisfied(prereqId, actionByCaseActionId, prereqMap, new Set())) {
          continue;
        }

        const prereq = actionByCaseActionId.get(prereqId);
        if (!prereq) continue;

        const ownPrereqsSatisfied = (prereqMap.get(prereqId) || []).every(p =>
          this.isPrereqSatisfied(p, actionByCaseActionId, prereqMap, new Set())
        );

        if (ownPrereqsSatisfied) {
          if (prereq.action_name && !names.includes(prereq.action_name)) {
            names.push(prereq.action_name);
          }
        } else {
          walk(prereqId);
        }
      }
    };

    walk(caseActionId);
    return names;
  }

  /**
   * Verifica, de forma TRANSITIVA, se um case_action pode ser considerado um
   * pré-requisito satisfeito para destravar as actions que dependem dele.
   *
   * Um pré-requisito só é satisfeito quando:
   *   1) a própria action está concluída (isActionComplete), E
   *   2) todos os pré-requisitos dela também estão satisfeitos (recursivamente).
   *
   * Sem o passo (2), um KYC reaproveitado de outro caso (ac_status='DONE') faria
   * o Contrato aparecer destravado mesmo com os questionários — pré-requisitos do
   * próprio KYC — ainda pendentes.
   *
   * @param visiting Guarda contra ciclo de dependências: se reencontrarmos um nó
   *   já em avaliação no caminho atual, tratamos como não satisfeito para
   *   interromper a recursão (o sort util já loga ciclos).
   */
  private isPrereqSatisfied(
    caseActionId: string,
    actionByCaseActionId: Map<string, EnrichedAction>,
    prereqMap: Map<string, string[]>,
    visiting: Set<string>
  ): boolean {
    // Proteção contra ciclo de dependências
    if (visiting.has(caseActionId)) {
      console.warn('[OA] Cycle detected while resolving prerequisites', { caseActionId });
      return false;
    }

    const action = actionByCaseActionId.get(caseActionId);
    if (!action) {
      console.warn('[OA] Prerequisite action not found', { caseActionId });
      return false;
    }

    // 1) a própria action precisa estar concluída
    if (!this.isActionComplete(action)) {
      return false;
    }

    // 2) e todos os pré-requisitos dela também precisam estar satisfeitos
    const ownPrereqs = prereqMap.get(caseActionId) || [];
    if (ownPrereqs.length === 0) {
      return true;
    }

    visiting.add(caseActionId);
    const allSatisfied = ownPrereqs.every(prereqId =>
      this.isPrereqSatisfied(prereqId, actionByCaseActionId, prereqMap, visiting)
    );
    visiting.delete(caseActionId);

    return allSatisfied;
  }

  /**
   * Verifica se uma action está completa baseado no ac_status
   * ✅ SIGNATURE aceita apenas 'SIGNED'
   */
  private isActionComplete(action: EnrichedAction): boolean {
    const deferredClaims = action.case_action_config?.simple_questionnaire?.deferred_claims;
    const deferred = Array.isArray(deferredClaims) && deferredClaims.includes(action.claim_id);

    switch (action.action_type) {
      case 'QUESTIONNAIRE':
        return action.ac_status === 'DONE' || action.ac_status === 'SUBMITTED';

      case 'KYC':
        return action.ac_status === 'DONE' || deferred;

      case 'SIGNATURE':
        return action.ac_status === 'SIGNED' || deferred;

      default:
        return false;
    }
  }

  /**
   * Converte EnrichedAction para Action para manter compatibilidade
   * com componentes filhos (oa-kyc, oa-questionnaire, oa-signature)
   */
  private enrichedToAction(enriched: EnrichedAction): Action {
    return {
      case_action_id: enriched.case_action_id,
      action_id: enriched.action_id,
      claim_id: enriched.claim_id,
      done_action_id: enriched.done_action_id,
      action_name: enriched.action_name,
      action_type: enriched.action_type,
      type: enriched.type,
      config: enriched.config,
      case_action_config: enriched.case_action_config,
      questionnaire_id: enriched.questionnaire_id,
      contract_id: enriched.contract_id,
      ac_status: enriched.ac_status,
      prstatus: enriched.prstatus,
      status: enriched.status,
      description: enriched.description,
      postponed: enriched.postponed,
      blocking_prereq_names: enriched.blocking_prereq_names,
    } as Action;
  }

  async onChange() {
    console.log('[OA] onChange - Reloading actions due to change event');
    await this.loadActions();
  }

  selectKycFromList(list: (Kyc_Check | null)[]): number {
    for (let i = 0; i < list.length; i++) {
      const kyc = list[i];
      if (kyc?.passed) {
        return i;
      }
    }
    console.log('[OA] No KYC passed.');

    if (list.length <= 1) {
      return 0;
    }

    let nl = list.filter((e) => !!e);

    nl = nl.sort((a, b) => {
      const val =
        new Date(b!.created_at!).getTime() - new Date(a!.created_at!).getTime();
      return val;
    });

    console.log('[OA] NL', nl);
    console.log('[OA] NL0', nl[0]!.id);
    console.log('[OA] list', list);

    const index = list.findIndex((v) => {
      return nl[0]!.id == v?.id;
    });

    console.log('[OA] Index', index);
    if (index == -1) return 0;

    return index;
  }
}
